import { ArrowUpRight } from "lucide-react";
import { Chapter, FadeUp } from "@/components/Reveal";
import { useLanguage } from "@/i18n/LanguageContext";

export default function Projects() {
  const { t } = useLanguage();
  const featured = t("projects.featured");
  const index = t("projects.index");
  const gallery = t("projects.gallery");

  return (
    <section id="work" className="py-28 md:py-40" data-testid="projects-section">
      <div className="mx-auto max-w-[1440px] px-6 md:px-12">
        <Chapter number="06" title={t("chapters.work")} />
        <FadeUp>
          <h2 className="mb-14 max-w-3xl text-balance font-syne text-3xl font-bold leading-[1.12] tracking-tight sm:text-4xl lg:text-[3.4rem] md:mb-20" data-testid="projects-heading">
            {t("projects.headingA")} <span className="text-[#F97316]">{t("projects.headingB")}</span>
          </h2>
        </FadeUp>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
          {featured.map((p, i) => (
            <FadeUp key={p.title} delay={i * 0.08} className={`group cursor-pointer ${p.span}`} data-testid={`project-card-${i + 1}`}>
              <div className={`relative overflow-hidden rounded-2xl border border-white/10 transition-colors duration-500 group-hover:border-[#F97316]/50 ${p.ratio}`}>
                {p.imgs ? (
                  <div
                    className={`absolute inset-0 grid gap-1 bg-[#0c0c0c] ${
                      p.imgs.length === 3 ? "grid-cols-3" : "grid-cols-2 grid-rows-2"
                    }`}
                  >
                    {p.imgs.map((src) => (
                      <img
                        key={src}
                        src={src}
                        alt={`${p.title} — ${p.client}`}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                      />
                    ))}
                  </div>
                ) : (
                  <img
                    src={p.img}
                    alt={`${p.title} — ${p.client}`}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-6 md:p-8">
                  <div>
                    <p className="mb-2 font-spacemono text-[10px] uppercase tracking-[0.25em] text-[#F97316]">{p.meta}</p>
                    <h3 className="font-syne text-xl font-bold tracking-tight text-white md:text-2xl">{p.title}</h3>
                    <p className="mt-1 text-xs text-white/60">{p.client}</p>
                    {p.stat && (
                      <p className="mt-2.5 font-spacemono text-[10px] uppercase tracking-[0.25em] text-white/50">{p.stat}</p>
                    )}
                    {p.scope && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {p.scope.map((s) => (
                          <span key={s} className="rounded-full border border-white/25 px-3 py-1 font-spacemono text-[9px] uppercase tracking-[0.15em] text-white/70">
                            {s}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <ArrowUpRight className="h-5 w-5 shrink-0 text-white/60 transition-[color,transform] duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-[#F97316]" />
                </div>
              </div>
            </FadeUp>
          ))}
        </div>

        <FadeUp className="mt-24 md:mt-32">
          <div className="mb-10 flex items-end justify-between gap-6">
            <h3 className="font-syne text-2xl font-bold tracking-tight md:text-3xl" data-testid="project-index-heading">
              {t("projects.recentHeading")}
            </h3>
            <span className="font-spacemono text-[10px] uppercase tracking-[0.3em] text-white/35">
              {t("projects.recentTag")}
            </span>
          </div>
        </FadeUp>
        <div data-testid="project-index">
          {index.map((p, i) => (
            <FadeUp key={p.title} delay={i * 0.03}>
              <div
                className={`group flex items-center gap-5 border-t border-white/10 py-5 md:gap-8 ${
                  i === index.length - 1 ? "border-b" : ""
                }`}
                data-testid={`project-index-row-${String(i + 1).padStart(2, "0")}`}
              >
                <span className="w-8 shrink-0 font-spacemono text-xs text-white/30 transition-colors duration-300 group-hover:text-[#F97316]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                {p.img && (
                  <div className="hidden h-14 w-24 shrink-0 overflow-hidden rounded-lg sm:block">
                    <img src={p.img} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  </div>
                )}
                <h4 className="flex-1 font-syne text-base font-bold tracking-tight text-white/85 transition-colors duration-300 group-hover:text-[#F97316] md:text-xl">
                  {p.title}
                </h4>
                <span className="hidden font-spacemono text-[10px] uppercase tracking-[0.2em] text-white/35 md:block">{p.type}</span>
                {p.pax && (
                  <span className="shrink-0 font-spacemono text-[10px] uppercase tracking-[0.2em] text-white/50">{p.pax}</span>
                )}
                {p.scope && (
                  <span className="hidden shrink-0 font-spacemono text-[10px] uppercase tracking-[0.2em] text-[#F97316]/70 md:block">
                    {p.scope.join(" · ")}
                  </span>
                )}
              </div>
            </FadeUp>
          ))}
        </div>

        <FadeUp className="mt-24 md:mt-32">
          <div className="mb-10 flex items-end justify-between gap-6">
            <h3 className="font-syne text-2xl font-bold tracking-tight md:text-3xl" data-testid="gallery-heading">
              {t("projects.galleryHeading")}
            </h3>
            <span className="font-spacemono text-[10px] uppercase tracking-[0.3em] text-white/35">
              {t("projects.galleryTag")}
            </span>
          </div>
        </FadeUp>
        <div className="columns-2 gap-4 md:columns-3 lg:columns-4" data-testid="gallery-grid">
          {gallery.map((g, i) => (
            <FadeUp key={g.img} delay={(i % 3) * 0.06} className="mb-4 break-inside-avoid">
              <div className="group overflow-hidden rounded-xl border border-white/10 transition-colors duration-500 hover:border-[#F97316]/40">
                <img
                  src={g.img}
                  alt={g.alt}
                  loading="lazy"
                  className="w-full transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                />
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}
